import { Box, Button, Checkbox, CheckboxProps, CircularProgress, FormHelperText, MenuItem, Select, SelectChangeEvent, Skeleton, SxProps, TextField, Theme, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { currencyConversion, formatCurrency } from "../../../utils/utilityFunctions";
import { BlackInfo, BlueDateIcon, BlueDropDownIcon, CheckIcon, UnCheckIcon } from "../../../assets/SvgIcons";
import { FormikErrors, useField } from "formik";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LoadingButton } from "@mui/lab";

interface PickUpInputProps {
    isLoading: boolean;
    inputType: 'number' | 'text';
    name?: string;
    defaultValue?: string | number;
    // setGlobalData?: React.Dispatch<React.SetStateAction<GetGloabalDataType>>
    error?: string;
    minValue?: number;
    setFieldValue?: (name: string, value: number) => void;
    value?: string | number;
    label?: string;
    disabled: boolean
}

const preventNegativeValues = (e: React.KeyboardEvent) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
const allowOnlyDecimals = (e: React.KeyboardEvent) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()


export const PickUpInput = (props: PickUpInputProps) => {
    const {
        isLoading,
        inputType,
        name = '',
        minValue,
        label,
        disabled
    } = props;
    const { palette, typography } = useTheme();
    const pickUpDigit = [
        { digit: 5, label: 'L', addedZero: '00,000' },
        { digit: 7, label: 'C', addedZero: '00,00,000' },
    ];
    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const [field, meta, helpers] = useField(name)
    const { setValue } = helpers
    const errorText = meta.error && meta.touched ? meta.error : '';


    const handleDigitalChange = (item: { digit: number; label: string; addedZero: string }) => {
        const value = Number((`${field.value}`?.length - 1) >= item.digit ? field.value : field.value + '0'.repeat(item.digit - (`${field.value}`?.length - 1)));
        setValue(value);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', opacity: disabled ? 0.5 : 1 }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500" fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || 'SIP Amount'}
                    </Typography>
                )}
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography
                        variant="body_400"
                        color={errorText ? palette.error[500] : palette.text.secondary}>
                        Min: {`${formatCurrency({ value: Number(minValue ?? 0), locale, currencyCode })}`}
                    </Typography>
                )}
            </Box>
            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        background: errorText ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        borderRadius: '10px',
                        gap: '10px',
                        height: '50px',
                    }}>
                    <Typography variant="body_500" mt={'-2px'} color={errorText ? palette.error[500] : palette.primary[500]}>
                        {String.fromCharCode(parseInt(currencySymbol, 16))}
                    </Typography>
                    <Box
                        {...field}
                        placeholder='Enter'
                        id={name}
                        // name={name}
                        // value={amount}
                        // defaultValue={defaultValue}
                        disabled={disabled}
                        type={inputType || 'number'}
                        component={'input'}
                        onPaste={e => e.preventDefault()}
                        onKeyDown={preventNegativeValues}
                        // onChange={handleInputChange}
                        onWheel={event => { event.currentTarget.blur(); }}
                        width={'100%'}
                        border={'none'}
                        sx={{
                            '::placeholder': {
                                color: errorText ? `${palette.error[500]}80` : palette.primary[500],
                                // marginTop: '8px',
                            },
                            overflow: 'hidden',
                            fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                            // WebkitAppearance: 'none'
                        }}
                        style={{
                            background: 'transparent',
                            outline: 'none',
                            fontFamily: typography.fontFamily,
                            ...typography.body_500,
                        }}
                        color={errorText ? palette.error[500] : palette.primary[500]}
                    />
                </Box>
            )}

            {errorText && (
                isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_400" color={palette.error[500]}>
                    {errorText || ''}
                </Typography>

            )}

            {isLoading ? (
                <Skeleton variant="text" animation="wave" width={200} />
            ) : (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
                    <BlackInfo />
                    <Typography variant="body_400" color={palette.text.secondary}>
                        {currencyConversion(locale, field.value || 0) || 'N/A'}
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', flexWrap: 'wrap' }}>
                {pickUpDigit?.map((item, i) => (
                    <Box
                        key={i}
                        component={'div'}
                        onClick={() => `${field.value}`?.length <= item.digit && Number(field.value) > 0 && handleDigitalChange(item)}
                        sx={{
                            background: palette.primary[50],
                            border: `1px solid ${palette.primary[500]}`,
                            borderRadius: '20px',
                            width: 'fit-content',
                            padding: '0px 10px',
                            cursor: `${field.value}`?.length <= item.digit && Number(field.value) > 0 ? 'pointer' : 'not-allowed',
                            opacity: `${field.value}`?.length <= item.digit && Number(field.value) > 0 ? 1 : .5,
                        }}>
                        {isLoading ? (
                            <Skeleton variant="text" animation="wave" width={100} sx={{ borderRadius: '8px' }} />
                        ) : (
                            <Typography color={palette.primary[500]} variant="body_500" >
                                {item.addedZero} | {item.label}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};



interface CustomeBlueDropDownProps {
    isLoading?: boolean;
    boxStyle?: any;
    label?: string;
    list?: Array<{ value: string; label: string }>;
    name?: string;
    disabled?: boolean;
    onChange?: ((event: SelectChangeEvent<any>, child: React.ReactNode) => void)
}
export const CustomeBlueDropDown = (props: CustomeBlueDropDownProps) => {
    const {
        isLoading,
        boxStyle,
        label,
        list = [],
        name = '',
        disabled = false,
        onChange
    } = props;
    const { palette, typography } = useTheme();


    const [field, meta] = useField(name)
    const errorText = meta.error && meta.touched ? meta.error : '';

    const handleChange = onChange || field.onChange;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500" color={palette.text.secondary}
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: disabled ? '0.5' : '1',
                        }}
                        fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}>
                        {label || 'Select Account'}
                    </Typography>
                )}
            </Box>

            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Select
                    {...field}
                    onChange={handleChange}
                    disableUnderline
                    variant="standard"
                    style={{
                        height: '50px',
                        borderRadius: '10px',
                        background: errorText ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        color: errorText ? palette.error[500] : palette.primary[500],
                        // opacity: disabled ? '0.5' : '1',
                        // borderRight: '2px solid ' + palette.text.secondary + 'b2',
                        ...(boxStyle || {}),
                    }}
                    sx={{
                        '.MuiSelect-standard': {
                            '&:focus': {
                                background: 'transparent',
                            },
                        },
                        fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                    }}
                    disabled={disabled}
                    IconComponent={(props) => (
                        <BlueDropDownIcon {...props} cursor={'pointer'} style={{ marginRight: '15px' }} />
                    )}>
                    <MenuItem key="a" value="a" disabled>
                        Select
                    </MenuItem>
                    {list?.map((item, index) => {
                        return (
                            <MenuItem key={index} value={item.value} >
                                {item.label}
                            </MenuItem>
                        );
                    })}
                    {list?.length === 0 && (
                        <MenuItem value={10} disabled>
                            No Data
                        </MenuItem>
                    )}
                </Select>
            )}
            {errorText &&
                (isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_400" color={palette.error[500]}>
                    {errorText || ''}
                </Typography>)
            }
        </Box>
    );
};

interface CustomeNumberInputProps {
    isLoading: boolean;
    textType?: 'number' | 'text';
    label?: string;
    minValue?: number;
    boxStyle?: SxProps<Theme>;
    minlabel?: string;
    warningText?: string;
    name?: string;
    defaultValue?: number | string;
    error?: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined;
    removeIcons?: boolean;
    infoAlign?: 'left' | 'center' | 'right';
    disable?: boolean
}
export const CustomeNumberInput = (props: CustomeNumberInputProps) => {
    const {
        isLoading,
        label,
        textType,
        minValue,
        boxStyle,
        minlabel,
        warningText,
        name = '',
        error,
        removeIcons,
        infoAlign = 'left',
        disable = false
    } = props;
    const { palette, typography } = useTheme();
    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    const [field, meta] = useField(name)
    const errorText = meta.error && meta.touched ? meta.error : '';


    // console.log('amount', amount)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
                component={'label'}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                {isLoading ? (
                    <Skeleton variant="text" animation="wave" width={100} />
                ) : (
                    <Typography variant="body_500"
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || ''}
                    </Typography>
                )}
                {minlabel &&
                    (isLoading ? (
                        <Skeleton variant="text" animation="wave" width={100} />
                    ) : (
                        <Typography variant="body_400"
                            fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}
                            lineHeight={1.3}
                            sx={{
                                textWrap: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: disable ? .5 : 1,

                            }}
                            color={errorText ? palette.error[500] : palette.text.secondary}>
                            {minlabel}
                        </Typography>
                    ))}
            </Box>
            {isLoading ? (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        background: errorText ? palette.error[50] : palette.primary[50],
                        padding: '15px',
                        borderRadius: '10px',
                        gap: '10px',
                        height: '50px',
                        opacity: disable ? .5 : 1,
                        ...boxStyle,
                    }}>
                    {!removeIcons && (
                        <Typography
                            variant="body_500"
                            color={errorText ? palette.error[500] : palette.primary[500]}>
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    )}
                    <Box
                        {...field}
                        component={'input'}
                        placeholder='Enter'
                        onPaste={e => e.preventDefault()}
                        disabled={disable}
                        onKeyDown={warningText?.toLowerCase() === 'units' ? allowOnlyDecimals : preventNegativeValues}
                        onWheel={event => { event.currentTarget.blur(); }}
                        type={textType || 'text'}
                        width={'100%'}
                        sx={{
                            '::placeholder': {
                                color: errorText ? `${palette.error[500]}80` : palette.primary[200],
                                // marginTop: '8px',
                            },
                            fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                            opacity: disable ? .9 : 1,
                            // cursor: disable ? 'not-allowed' : 'text',

                        }}
                        border={'none'}
                        style={{
                            background: 'transparent',
                            outline: 'none',
                            fontFamily: typography.fontFamily,
                            ...typography.body_500,
                        }}
                        color={errorText ? palette.error[500] : palette.primary[500]}
                    />
                </Box>
            )}
            {minValue?.toString() ? (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
                    <Typography
                        variant="body_400"
                        sx={{
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            opacity: disable ? .5 : 1
                        }}
                        color={errorText ? palette.error[500] : palette.text.secondary}>
                        Min. number of installments is {minValue}
                    </Typography>
                </Box>
            ) : errorText && (
                isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_400" color={palette.error[500]}>
                    {errorText || ''}
                </Typography>)}
            {warningText === 'amount' &&
                (isLoading ? (
                    <Skeleton variant="text" animation="wave" width={200} />
                ) : (
                    field.value ? (
                        <Box
                            // component={'label'}
                            sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center', justifyContent: infoAlign, opacity: disable ? .5 : 1 }}
                        >
                            <BlackInfo />
                            <Typography variant="body_400" color={palette.text.secondary}>
                                {currencyConversion(locale, typeof field.value === 'string' ? parseInt(field.value) : field.value) || 'Zero'}
                            </Typography>
                        </Box>
                    ) : null
                ))}
            {/* {errorText && (
                isLoading ? <Skeleton variant="text" animation="wave" width={200} /> : <Typography variant="body_500" color={palette.error[500]}>
                    {errorText || ''}
                </Typography>

            )} */}
        </Box>
    );
};

interface CustomeBlueDatePickerProps {
    label?: string;
    name: string;
    // handleOnChange?: (name: string, value: string) => void;
    // defaultValue?: string;
    // error?: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined;
    disablePast?: boolean;
    isLoading?: boolean;
    disabled?: boolean
    // disableDateList?: Array<number>;
    shouldDisableDate?: (date: dayjs.Dayjs) => boolean;
    minDate?: Dayjs
}

export const CustomeBlueDatePicker = (props: CustomeBlueDatePickerProps) => {
    const { palette, typography } = useTheme();
    const { label, name, disablePast, isLoading,
        disabled = false,
        //disableDateList = [],
        shouldDisableDate,
        minDate } = props;

    const [field, meta, helpers] = useField(name)
    const { setValue, setTouched } = helpers
    const errorText = meta.error && meta.touched ? meta.error : '';


    const onChange = (date: Date | any) => {
        setValue(dayjs(date).toDate());
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isLoading && (
                <Skeleton variant="text" animation="wave" width={100} />
            )}
            {!isLoading && (
                <Box
                    component={'label'}
                    sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Typography variant="body_500" fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }} color={palette.text.secondary}>
                        {label || 'SIP Date'}
                    </Typography>
                </Box>
            )}
            {isLoading && (
                <Skeleton variant="rectangular" height={50} sx={{ borderRadius: '10px' }} />
            )}
            {!isLoading &&
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer
                        components={['DatePicker']}
                        sx={{
                            paddingTop: 0,
                            borderRadius: '10px',
                            background: errorText ? palette.error[50] : palette.primary[50],
                            '&:focus': {
                                border: 'none',
                            },
                            '&:hover': {
                                border: 'none',
                            },
                        }}>
                        <DatePicker
                            {...field}
                            disabled={disabled}
                            label=""
                            slots={{
                                openPickerIcon: BlueDateIcon,
                            }}
                            slotProps={{
                                textField: {
                                    onBlur: () => setTouched(true),
                                }
                            }}
                            disablePast={disablePast}
                            onChange={onChange}
                            value={dayjs(field.value)}
                            format="DD/MM/YYYY"
                            shouldDisableDate={shouldDisableDate}
                            // shouldDisableDate={(date: any) => {

                            //     console.log('!disableDateList?.includes(dayjs(date).date()', !disableDateList?.includes(dayjs(date).date()))

                            //     return disableDateList?.length > 0 && !disableDateList?.includes(dayjs(date).date());
                            // }}
                            sx={{
                                fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                                width: '100%',
                                '& input': {
                                    ...typography.body_500,
                                    fontFamily: typography.fontFamily,
                                    height: '50px',
                                    padding: '0 15px',
                                    color: errorText ? palette.error[500] : palette.primary[500],
                                },
                                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { border: 'none' }, // at page load
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 'none' }, // at hover state
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                }, // at focused state
                            }}
                            minDate={minDate}
                        />
                    </DemoContainer>
                </LocalizationProvider>
            }
            {errorText &&
                (
                    isLoading ? <Skeleton variant="text" animation="wave" width={200} /> :
                        <Typography variant="body_400" color={palette.error[500]}>
                            {errorText || ''}
                        </Typography>
                )}
        </Box>
    );
};

interface CheckBoxProps {
    name: string
    isLoading: boolean
}
export const FormCheckBox = (props: CheckboxProps & CheckBoxProps) => {
    const { name, isLoading, onChange, disabled } = props
    const { palette, typography } = useTheme()
    const [field, meta] = useField(name)

    const errorText = meta.error && meta.touched ? meta.error : '';

    const handleChange = onChange || field.onChange;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '0px', opacity: disabled ? 0.5 : 1 }}>
            {isLoading ? <Skeleton variant='text' animation="wave" width={100} /> :
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
                        <Checkbox
                            {...field}
                            name={field.name}
                            disabled={disabled}
                            onChange={handleChange}
                            checked={field.value}
                            checkedIcon={<CheckIcon />}
                            icon={<UnCheckIcon />}
                        />
                        <Typography variant='body_500' color={palette.text.secondary}>Entire Units</Typography>
                    </Box>
                    <FormHelperText sx={{ ...typography.body_400, color: palette.error[500] }} >{errorText}</FormHelperText>
                </>
            }
        </Box>
    )

}


interface CustomeButtonProps {
    btnText: string
    onClick?: () => void;
    disabled?: boolean;
    type?: "button" | "reset" | "submit";
    boxStyle?: any;
    icon?: JSX.Element;
    isLoading?: boolean;
    variant?: 'text' | 'outlined' | 'contained';
}

export const CustomeModalButton = (props: CustomeButtonProps) => {
    const { palette, typography } = useTheme();
    const { btnText, onClick, boxStyle, isLoading, disabled = false, type = 'button', variant = 'contained' } = props;
    return (
        <LoadingButton
            type={type}
            disabled={disabled}
            loading={isLoading}
            fullWidth
            {...(onClick ? { onClick } : {})}
            // onClick={() => onClick()}
            variant={variant}
            sx={{
                borderRadius: { lg: '10px', md: '10px', sm: '4px', xs: '4px' },
                height: { lg: '50px', md: '50px', sm: '40px', xs: '40px' },
                ...typography.body_600,
                ...boxStyle,
            }}>
            {btnText}
        </LoadingButton>
    );
};